import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Input, Tag, message } from 'antd'
import SearchIcon from '../icons/SearchIcon'
import {
  calcCharactersLen,
  debounce,
  filterIllegalCharacters,
  raplaceEmoji as replaceEmoji,
  sliceCharactersLen
} from '../../../../../utils/utils'
import {
  useSearchHistory,
  SearchHistoryItem
} from '../../../../../app/hooks/useSearchKeywordsHistory'
import { useFilterMemberHistory } from '../../../../../app/hooks/useFilterMemberHistory'
import { DepartmentMemberV2 } from '../../../../../global'
import { SelectedDepartment } from '../../../DepartmentMemberPickerV2'
import { FilterState } from '../../types'
import dayjs from 'dayjs'
import '../../styles/tags-input.scss'
import { trackPicsPageClick } from '../../utils/sensorUtils'

const GUIDEWORD_LIMIT = 5
const PLACEHOLDER_ROTATE_INTERVAL = 3000
const PLACEHOLDER_ROTATE_LINE_HEIGHT = 22
const DEFAULT_PLACEHOLDER = '水印文字, 人名, 地点...一句话搜索'

const formatRotatingGuideText = (guide: string) => {
  if (guide === '可用空格进行多关键词搜索') {
    return guide
  }
  return `试试输入"${guide}"`
}

type MemberHistoryItem = SelectedDepartment | DepartmentMemberV2

// 获取当月日期范围文本
const getGuideDateRangeText = (): string => {
  const today = dayjs()
  if (today.date() === 1) {
    const prevMonthStart = today.clone().subtract(1, 'month').startOf('month')
    const prevMonthEnd = today.clone().subtract(1, 'month').endOf('month')
    return `${prevMonthStart.format('M月D日')}-${prevMonthEnd.format('M月D日')}`
  }
  const monthStart = today.clone().startOf('month')
  return `${monthStart.format('M月D日')}-今天`
}

// 获取上月月份文本
const getGuideMonthText = (): string => {
  const today = dayjs()
  const targetMonth =
    today.date() === 1 ? today.clone().subtract(1, 'month') : today
  return `${targetMonth.format('M月')}`
}

// 构建成员提示词
const buildMemberGuideWords = (history: MemberHistoryItem[]): string[] => {
  const textSet = new Set<string>()
  const dateRangeText = getGuideDateRangeText()
  history.forEach((item) => {
    const name = item?.name?.trim()
    if (!name) return
    const text = `${name}${dateRangeText}拍的照片`
    if (!textSet.has(text)) {
      textSet.add(text)
    }
  })
  return Array.from(textSet)
}

// 构建关键词提示词
const buildKeywordGuideWords = (history: SearchHistoryItem[]): string[] => {
  const textSet = new Set<string>()
  const monthText = getGuideMonthText()
  history.forEach((item) => {
    if (!item || !item.word || item.isNlp) return
    const keyword = item.word.trim()
    if (!keyword) return
    const text = `${monthText}拍的${keyword}`
    if (!textSet.has(text)) {
      textSet.add(text)
    }
  })
  return Array.from(textSet)
}

interface TagsInputWithIconProps {
  tags: string[]
  inputValue: string
  onChange: (value: string[]) => void
  onInputChange: (value: string) => void
  onSensorOfClickEvent: (clickItem: string, restProperties: any) => void
  onFocusChange?: (isFocused: boolean) => void
  aiPlaceholder?: string | null
  // preserveInput 代表当前是否需要变成tag
  onSubmit?: (
    latestTags?: string[],
    overrideFilter?: Partial<FilterState>,
    options?: { preserveInput?: boolean }
  ) => void
}

const debouncedOverInputMessage = debounce(
  () => {
    message.warning('最多可输入30个汉字，60个字符!')
  },
  1000,
  true
)

const debouncedOverKeywordsLengthMessage = debounce(
  () => {
    message.warning('最多支持3个关键词!')
  },
  1000,
  true
)

const defaultInputWidth = 400

const TagsInputWithIcon: React.FC<TagsInputWithIconProps> = ({
  tags = [],
  inputValue,
  onChange = () => {
    /* noop */
  },
  onInputChange = () => {
    /* noop */
  },
  onSensorOfClickEvent = () => {
    /* noop */
  },
  onFocusChange = () => {
    /* noop */
  },
  aiPlaceholder = null,
  onSubmit
}) => {
  const hiddenValueRef = useRef<HTMLSpanElement | null>(null)
  const [inputWidth, setInputWidth] = useState(defaultInputWidth)
  const [placeholderGuides, setPlaceholderGuides] = useState<string[]>([])
  const [guideRotateIndex, setGuideRotateIndex] = useState(0)
  const [rotatorSkipTransition, setRotatorSkipTransition] = useState(false)
  const guideRotateIndexRef = useRef(0)
  const { history: searchHistory = [] } = useSearchHistory()
  const { history: filterMemberHistory = [] } = useFilterMemberHistory()
  const inputRef = useRef<any>(null)
  const lastAiPlaceholderRef = useRef<string | null>(null)

  useEffect(() => {
    setInputWidth(defaultInputWidth)
  }, [tags])

  const showInputTips = useMemo(() => {
    return inputValue && tags.length === 0
  }, [inputValue, tags])

  // 构建提示词
  useEffect(() => {
    const memberGuides = buildMemberGuideWords(filterMemberHistory)
    const keywordGuides = buildKeywordGuideWords(searchHistory)
    const combined = [
      '可用空格进行多关键词搜索',
      '我拍的照片',
      `${new Date().getMonth() + 1}月1日 - 今天的照片`,
      '说说你想搜什么时候的照片？',
      ...memberGuides,
      ...keywordGuides
    ].slice(0, GUIDEWORD_LIMIT + 1)
    setPlaceholderGuides(combined)
  }, [filterMemberHistory, searchHistory])

  useEffect(() => {
    setGuideRotateIndex(0)
    guideRotateIndexRef.current = 0
  }, [placeholderGuides])

  // 轮播展示提示词（文案由覆盖层 + translateY 滚动切换）
  useEffect(() => {
    if (placeholderGuides.length === 0) {
      return
    }
    const timer = setInterval(() => {
      const len = placeholderGuides.length
      const prev = guideRotateIndexRef.current
      if (len <= 1) {
        return
      }
      const next = prev === len - 1 ? 0 : prev + 1
      if (prev === len - 1) {
        setRotatorSkipTransition(true)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setRotatorSkipTransition(false)
          })
        })
      }
      guideRotateIndexRef.current = next
      setGuideRotateIndex(next)
    }, PLACEHOLDER_ROTATE_INTERVAL)
    return () => clearInterval(timer)
  }, [placeholderGuides])

  const showRotatingPlaceholderOverlay = useMemo(() => {
    return (
      tags.length === 0 &&
      !aiPlaceholder &&
      !inputValue &&
      placeholderGuides.length > 0
    )
  }, [tags.length, aiPlaceholder, inputValue, placeholderGuides.length])

  useEffect(() => {
    if (tags.length !== 0 || !aiPlaceholder) {
      return
    }
    if (lastAiPlaceholderRef.current !== aiPlaceholder) {
      trackPicsPageClick({
        clickItem: 'Ai搜索推荐曝光'
      })
      lastAiPlaceholderRef.current = aiPlaceholder
    }
  }, [tags.length, aiPlaceholder])

  const inputNativePlaceholder = useMemo(() => {
    if (tags.length === 0 && aiPlaceholder) {
      return aiPlaceholder
    }
    if (showRotatingPlaceholderOverlay) {
      return ''
    }
    if (tags.length === 0) {
      return DEFAULT_PLACEHOLDER
    }
    if (tags.length <= 2) {
      return '加搜索词'
    }
    return ''
  }, [tags.length, aiPlaceholder, showRotatingPlaceholderOverlay])

  const handleTagRemove = useCallback(
    (index: number) => {
      const newValue = tags.filter((item, i) => i !== index)
      onChange(newValue)
    },
    [tags, onChange]
  )

  const handleSubmitTag = useCallback(() => {
    if (inputValue) {
      const nextTags = Array.from(new Set([...tags, inputValue.trim()]))
      onChange(nextTags)
      onInputChange('')
      setInputWidth(defaultInputWidth)
      return nextTags
    }
    return tags
  }, [inputValue, onChange, onInputChange, tags])

  const handleInputChange = useCallback(
    (e) => {
      if (tags.length >= 3) {
        debouncedOverKeywordsLengthMessage()
        return
      }
      const textValue = e.target.value
      let filtedValue = filterIllegalCharacters(textValue, ['/'])
      filtedValue = replaceEmoji(filtedValue)
      if (filtedValue.length < textValue.length) {
        message.warning('特殊字符已过滤!')
      }

      if (filtedValue.trim() === '') {
        setInputWidth(defaultInputWidth)
        onInputChange('')
        return
      }

      if (!calcCharactersLen(filtedValue, 60)) {
        if (filtedValue.slice(-1) === ' ') {
          handleSubmitTag()
          return
        }
        debouncedOverInputMessage()
        onInputChange(sliceCharactersLen(filtedValue, 60))
      } else {
        if (filtedValue.slice(-1) === ' ') {
          handleSubmitTag()
          return
        }
        onInputChange(filtedValue)
      }
    },
    [handleSubmitTag, onInputChange, tags]
  )

  const handleKeyDown = useCallback(
    (e) => {
      if (inputValue === '' && e.key === 'Backspace') {
        handleTagRemove(tags.length - 1)
        e.preventDefault()
      }
    },
    [handleTagRemove, inputValue]
  )

  const handleInputFocus = useCallback(() => {
    onSensorOfClickEvent('searchContent', {})
    onFocusChange(true)
  }, [onSensorOfClickEvent, onFocusChange])

  const handleInputBlur = useCallback(() => {
    // 单条件（没有已存在的 tag）：blur 不触发搜索，只通知失焦并保留输入
    if (tags.length === 0) {
      onFocusChange(false)
      return
    }
    handleSubmitTag()
    onFocusChange(false)
  }, [handleSubmitTag, onFocusChange, tags.length])

  useEffect(() => {
    if (!inputValue) {
      setInputWidth(defaultInputWidth)
      return
    }
    if (hiddenValueRef.current) {
      setInputWidth(hiddenValueRef.current.offsetWidth + 4)
    }
  }, [inputValue])

  return (
    <div className="keywords-input keywords-input--with-tags">
      <span className="keywords-input-prefix">
        <SearchIcon />
      </span>
      <div
        className="tags-input-wrapper"
        onClick={(e) => {
          // 如果用户点击了 Tag 或其关闭按钮，不抢占点击事件
          const target = e.target as HTMLElement
          if (target.closest('.ant-tag')) {
            return
          }
          // 如果已经点击在输入框上，不做处理
          if (target.closest('input')) {
            return
          }
          // 其它情况聚焦到输入框
          if (
            inputRef.current &&
            typeof inputRef.current.focus === 'function'
          ) {
            inputRef.current.focus()
          }
        }}
      >
        {tags.map((item, index) => {
          return (
            <Tag key={index} closable onClose={() => handleTagRemove(index)}>
              {item}
            </Tag>
          )
        })}
        <div
          className="tags-input-field-wrap"
          style={{
            position: 'relative',
            width: inputWidth,
            flexShrink: 0
          }}
        >
          {showRotatingPlaceholderOverlay && (
            <div className="tags-input-placeholder-rotator" aria-hidden>
              <div
                className={
                  'tags-input-placeholder-rotator__track' +
                  (rotatorSkipTransition
                    ? ' tags-input-placeholder-rotator__track--instant'
                    : '')
                }
                style={{
                  transform: `translateY(-${
                    guideRotateIndex * PLACEHOLDER_ROTATE_LINE_HEIGHT
                  }px)`
                }}
              >
                {placeholderGuides.map((guide, i) => (
                  <div
                    key={`${i}-${guide.slice(0, 24)}`}
                    className="tags-input-placeholder-rotator__item"
                  >
                    {formatRotatingGuideText(guide)}
                  </div>
                ))}
              </div>
            </div>
          )}
          <Input
            style={{ width: inputWidth }}
            placeholder={inputNativePlaceholder}
            value={inputValue}
            onChange={handleInputChange}
            ref={inputRef}
            onPressEnter={() => {
              // 与 onKeyDown 的 Enter 行为保持一致：单条件只触发搜索，不转 tag
              if (tags.length === 0) {
                onSubmit &&
                  onSubmit(undefined, undefined, { preserveInput: true })
                return
              }
              const latestTags = handleSubmitTag()
              onSubmit && onSubmit(latestTags)
            }}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
          />
        </div>
        <span
          style={{ visibility: 'hidden' }}
          className="hidden-value"
          ref={hiddenValueRef}
        >
          {inputValue}
        </span>
        {showInputTips && (
          <span className="input-tips">按空格可加多个搜索词</span>
        )}
      </div>
    </div>
  )
}

export default TagsInputWithIcon
